// Toast.tsx - Custom toast notification component
import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { ToastProps } from '../../types';

const Toast: React.FC<ToastProps> = ({ message, type = 'success', isVisible, onClose, duration = 5000 }) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const getToastStyles = (): { background: string; color: string } => {
    switch (type) {
      case 'success':
        return {
          background: '#02c39a',
          color: '#fff'
        };
      case 'error':
        return {
          background: '#dc2626',
          color: '#fff'
        };
      default:
        return {
          background: '#02c39a',
          color: '#fff'
        };
    }
  };

  const getIcon = (): React.ReactNode => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6" />;
      case 'error':
        return <AlertCircle className="w-6 h-6" />;
      default:
        return <CheckCircle className="w-6 h-6" />;
    }
  };

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] animate-in slide-in-from-top duration-300">
      <div 
        className="shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)] rounded-lg h-16 overflow-hidden flex items-center min-w-[400px] transition-all duration-300"
        style={{ 
          ...getToastStyles(),
          fontFamily: 'Inter, sans-serif'
        }}
      >
        <div className="flex-1 flex items-center p-4 gap-4">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="flex-1 relative leading-5 font-semibold text-sm overflow-hidden text-ellipsis line-clamp-2">
            {message}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;